import React, { useContext, useEffect, useState, FunctionComponent, useCallback } from 'react';

import { navigate } from '@reach/router';
import moment from 'moment';

import { IMetaFormat, IMetaFormatBluePrint } from '../../../../store/admin/roundFormats/store';
import { GlobalContext } from '../../../../store';
import { IEventRound } from '../../../../store/admin/eventRounds/store';

import { FormConsumer, FormProvider, IFormAttributes } from '../../../../components/Forms';
import GeneralRoundInfo from '../../../../components/GeneralRoundInfo';
import { lazyEqual } from '../../../../contrib/utils';
import { EventWizardStepTitle } from '../Components/EventWizardStepTitle/EventWizardStepTitle';
import TeeBoxAssignments from './TeeBoxAssignments/TeeBoxAssignments';
import { TeeSheetSetup } from './TeeSheetSetup/TeeSheetSetup';

interface IProps {
  eventId: string;
  roundId: string;
}

export const onExit = (clubId: number) => navigate(`/staff/club/${clubId}`);

const RoundBasics: FunctionComponent<IProps> = props => {
  const { roundId, eventId } = props;

  const {
    adminEventRounds,
    adminEvents,
    adminRoundFormats,
    adminCourses: courses,

    actions: {
      adminEvents: { editRound },
      adminRoundFormats: { updateRoundFormat, getRoundFormats }
    }
  } = useContext(GlobalContext);

  const event = adminEvents[Number(eventId)];
  const round = adminEventRounds[Number(roundId)];
  const clubId = event ? event.club : 0;

  const [ctpFormatToUpdate, setCTPFormatToUpdate] = useState<IMetaFormat>();

  const onContinue = () =>
    navigate(
      `/staff/club/${clubId}/event/${eventId}/round/${roundId}/${
        event.allowTeams ? 'teams' : 'teesheet'
      }`
    );

  useEffect(() => {
    if (adminRoundFormats.roundFormats[Number(eventId)]) {
      setCTPFormatToUpdate(
        adminRoundFormats.roundFormats[Number(eventId)].find(
          format => format.format.prefix === 'ctp'
        )
      );
    }
  }, [adminRoundFormats, eventId]);

  useEffect(() => {
    getRoundFormats(clubId, eventId);
  }, [clubId, eventId]);

  // useEffect(() => {
  //   if (event && event.rounds) {
  //     const newRoundTeesAssignation: IRoundTeesAssignation[] = event.rounds.reduce(
  //       (assignation: IRoundTeesAssignation[], r): IRoundTeesAssignation[] => [
  //         ...assignation,
  //         ...r.teeAssignments.map((tee: ITeeAssignment) => ({
  //           clubId,
  //           eventId: Number(eventId),
  //           flightId: tee.flight,
  //           roundId: tee.round,
  //           roundTeeId: tee.id,
  //           tee: tee.tee
  //         }))
  //       ],
  //       []
  //     );

  //     if (!lazyEqual(newRoundTeesAssignation, roundTeesAssignation)) {
  //       setFlightAssignation(newRoundTeesAssignation);
  //     }
  //   }
  // }, [event]);

  const save = async (newRound: IEventRound, noAdvance?: boolean) => {
    if (!lazyEqual(round, newRound)) {
      const response = await editRound(clubId, event, newRound as any);

      await saveCTP();

      if (!response.errors && !noAdvance) {
        // TODO: Fix errors and edge condition
        onContinue();
      }
    } else {
      if (!noAdvance) {
        // TODO: Fix errors and edge condition
        onContinue();
      }
    }
  };

  const saveCTP = useCallback(() => {
    if (ctpFormatToUpdate) {
      const formatBlueprint: IMetaFormatBluePrint = {
        ...ctpFormatToUpdate,
        format: ctpFormatToUpdate.format.id
      };

      const formatId = formatBlueprint.id;

      delete formatBlueprint.id;

      return updateRoundFormat(clubId, eventId, formatId, formatBlueprint);
    }
  }, [ctpFormatToUpdate]);

  const defaultRound = {
    ...round,
    playersPerTee: round ? round.playersPerTee || 4 : 4,
    teeFrequency:
      round && !!round.teeFrequency ? String(moment.duration(round.teeFrequency).asMinutes()) : '8'
  };

  return !event || !round ? (
    <></>
  ) : (
    <div className={`admin-wrapper round-setup`}>
      <div className="round-basics">
        <FormProvider
          defaultValues={defaultRound}
          onSubmit={save}
          validations={{
            start: 'required',
            totalPlayers: 'required',
            totalRounds: 'required'
          }}
        >
          <FormConsumer>
            {({ values, formActions: { setValue, toggleValue }, errors }: IFormAttributes) => (
              <div className="event-wizard-page form">
                <EventWizardStepTitle
                  buttonText="SAVE & CONTINUE"
                  buttonDisabled={
                    errors.FORM ||
                    (!values.shotgunStart && Number(values.teeFrequency) < 1) ||
                    values.playersPerTee < 2
                  }
                  title="GETTING STARTED"
                />

                <GeneralRoundInfo
                  ctpFormatToUpdate={ctpFormatToUpdate}
                  displayHandicapAllowance={true}
                  roundId={Number(roundId)}
                  setCTPFormatToUpdate={setCTPFormatToUpdate}
                  setValue={setValue}
                  values={values}
                />

                <TeeSheetSetup
                  errors={errors}
                  event={event}
                  round={values}
                  setValue={setValue}
                  toggleValue={toggleValue}
                  values={values}
                />

                <TeeBoxAssignments
                  setTeeAssignationsNotSaved={() => null}
                  course={courses.find(c => c.id === values.course.id)}
                />
              </div>
            )}
          </FormConsumer>
        </FormProvider>
      </div>
    </div>
  );
};

export { RoundBasics };
